import ReactDOM from 'react-dom';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useStudentView } from '../../contexts/StudentViewContext';
import { authFetch, authPost } from '../../utils/api';
import { formatDate } from '../../utils/dates';
import { StudentBanner } from '../../components/cfi/StudentBanner';
import { TC, TBadge, TCard, TOverlay } from './trainTheme';
import { CourseCatalogue } from './CourseCatalogue';
import { ACSSection } from './ACSSection';

// ── Helpers ──────────────────────────────────────────────────────────────────

function scoreColor(pct) {
  if (pct == null) return 'var(--text-muted)';
  if (pct >= 80) return '#065f46';
  if (pct >= 50) return '#1e3a8a';
  return '#713f12';
}
function scoreBg(pct) {
  if (pct == null) return 'rgba(117,143,168,0.15)';
  if (pct >= 80) return '#bbf7e0';
  if (pct >= 50) return '#bfdbfe';
  return '#fef08a';
}
function scoreBorder(pct) {
  if (pct == null) return 'rgba(117,143,168,0.35)';
  if (pct >= 80) return '#10b981';
  if (pct >= 50) return '#3b82f6';
  return '#ca8a04';
}

function ScoreBadge({ value, label, pct }) {
  return (
    <div style={{
      width: 62, height: 62, borderRadius: 14, flexShrink: 0,
      background: scoreBg(pct), border: `1px solid ${scoreBorder(pct)}`,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    }}>
      <span style={{ fontSize: 19, fontWeight: 700, color: scoreColor(pct), fontFamily: 'var(--font-mono)', lineHeight: 1.1 }}>{value}</span>
      <span style={{ fontSize: 10, fontWeight: 600, color: scoreColor(pct), textTransform: 'uppercase', letterSpacing: '0.06em', opacity: 0.75, marginTop: 1 }}>{label}</span>
    </div>
  );
}

function TileChevron({ open }) {
  return (
    <span style={{ fontSize: 11, color: 'var(--text-muted)', flexShrink: 0, transition: 'transform 0.18s', display: 'inline-block', transform: open ? 'rotate(180deg)' : 'none' }}>▾</span>
  );
}

function LabelBarred({ color, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
      <div style={{ width: 3, borderRadius: 2, background: color, alignSelf: 'stretch', minHeight: 18, flexShrink: 0 }} />
      <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{children}</span>
    </div>
  );
}

// ── Gap card ─────────────────────────────────────────────────────────────────
function GapRow({ gap, onTap }) {
  const dotColor = gap.is_gap ? TC.amber : TC.green;
  return (
    <div onClick={() => onTap(gap)} style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '9px 14px', borderBottom: '1px solid var(--border)',
      cursor: 'pointer',
    }}>
      <div style={{ width: 8, height: 8, borderRadius: '50%', background: dotColor, opacity: gap.is_gap ? 1 : 0.7, flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{gap.label}</div>
        <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 1 }}>{gap.demonstrated}</div>
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'right', flexShrink: 0 }}>{gap.minimum}</div>
      <div style={{ fontSize: 13, color: 'var(--text-muted)', flexShrink: 0 }}>›</div>
    </div>
  );
}

// ── Score weakness row ────────────────────────────────────────────────────────
function WeaknessRow({ weakness, isWeak, onTap }) {
  const dotColor = isWeak === false ? '#10b981' : TC.amber;
  return (
    <div onClick={() => onTap(weakness)} style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '9px 14px', borderBottom: '1px solid var(--border)',
      cursor: 'pointer',
    }}>
      <div style={{ width: 8, height: 8, borderRadius: '50%', background: dotColor, opacity: isWeak === false ? 0.7 : 1, flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{weakness.category}</div>
      </div>
      <div style={{ fontSize: 12, fontWeight: 700, fontFamily: 'var(--font-mono)', color: scoreColor(weakness.avg_pct) }}>{weakness.avg_pct}%</div>
      <div style={{ fontSize: 13, color: 'var(--text-muted)', flexShrink: 0 }}>›</div>
    </div>
  );
}

// ── Build gaps from trainData ─────────────────────────────────────────────────
function buildGaps(trainData) {
  const skillsMax = trainData?.skills_max  || {};
  const skills90d = trainData?.skills_90d  || {};
  const flightCt  = trainData?.flight_count_90d || 0;
  const pmins     = trainData?.pilot_minimums   || {};
  const gaps = [];

  if (pmins.crosswind_kt) {
    const limit = parseFloat(pmins.crosswind_kt);
    const demo  = skillsMax['Crosswind'] || 0;
    gaps.push({ id: 'crosswind', label: 'Crosswind', minimum: `${limit} kt limit`,
      demonstrated: (skills90d['Crosswind'] || 0) > 0 ? `${skills90d['Crosswind']} in 90d${demo > 0 ? ` (${demo.toFixed(1)} kt max)` : ''}` : 'None in 90d',
      is_gap: (skills90d['Crosswind'] || 0) === 0,
      detail: `Your ${limit} kt crosswind limit is a ceiling you stay under, not a target. ` + ((skills90d['Crosswind'] || 0) > 0
        ? `You flew crosswind approaches in the last 90 days${demo > 0 ? ` (max ${demo.toFixed(1)} kt)` : ''} -- current.`
        : 'No crosswind approaches in the last 90 days.'),
    });
  }
  if (pmins.gust_kt) {
    const limit = parseFloat(pmins.gust_kt);
    const demo  = skillsMax['Gust'] || 0;
    gaps.push({ id: 'gust', label: 'Gusty Conditions', minimum: `${limit} kt gusts`,
      demonstrated: (skills90d['Gust'] || 0) > 0 ? `${skills90d['Gust']} in 90d${demo > 0 ? ` (${demo.toFixed(1)} kt max)` : ''}` : 'None in 90d',
      is_gap: (skills90d['Gust'] || 0) === 0,
      detail: `Your ${limit} kt gust limit is a ceiling you stay under, not a target. ` + ((skills90d['Gust'] || 0) > 0
        ? `You flew in gusty conditions in the last 90 days${demo > 0 ? ` (max ${demo.toFixed(1)} kt)` : ''} -- current.`
        : 'No gusty-condition flights in the last 90 days.'),
    });
  }
  if (pmins.ceiling) {
    const limitStr = pmins.ceiling;
    const limitNum = parseFloat(limitStr) || (limitStr === 'IFR' ? 500 : null);
    if (limitNum) {
      const ceilCt = (skills90d['IFR'] || 0) + (skills90d['LIFR'] || 0);
      gaps.push({ id: 'ceiling', label: 'Ceiling / IMC', minimum: limitStr,
        demonstrated: ceilCt > 0 ? `${ceilCt} IFR in 90d` : 'None in 90d',
        is_gap: ceilCt === 0,
        detail: `Your ${limitStr} ceiling minimum is a floor you stay above, not a target. ` + (ceilCt > 0
          ? `You flew in IFR conditions in the last 90 days -- current.`
          : 'No IFR flights in the last 90 days.'),
      });
    }
  }
  const nightCt = skills90d['Night Flight'] || 0;
  gaps.push({ id: 'night', label: 'Night Currency (61.57b)', minimum: '3 landings / 90d',
    demonstrated: `${nightCt} night flight${nightCt !== 1 ? 's' : ''} in 90d`,
    is_gap: nightCt < 3,
    detail: `14 CFR 61.57(b) requires 3 full-stop night landings in 90 days to carry passengers at night. You have ${nightCt}.` +
      (nightCt >= 3 ? ' Current.' : ' Schedule a night currency flight.'),
  });
  gaps.push({ id: 'frequency', label: 'Flight Frequency', minimum: '10 flights / 90d',
    demonstrated: `${flightCt} flight${flightCt !== 1 ? 's' : ''} in 90d`,
    is_gap: flightCt < 10,
    detail: flightCt >= 10
      ? `${flightCt} flights in 90 days — solid frequency.`
      : `${flightCt} flights in 90 days. Aim for 10+ to maintain sharpness.`,
  });
  return gaps;
}

// ── Detail sheet ──────────────────────────────────────────────────────────────
function DetailSheet({ item, onClose, token, viewingStudent, onNavigate }) {
  const [flights, setFlights] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!item || !token) return;
    const isGap = item.is_gap != null;
    // Flight Frequency has no skill tag — nothing to query
    if (isGap && !item.skillName) { setFlights(null); setLoading(false); return; }
    setFlights(null);
    setLoading(true);
    const params = new URLSearchParams({
      type: isGap ? 'skill' : 'category',
      name: isGap ? item.skillName : item.category,
    });
    if (viewingStudent) params.set('as_student', viewingStudent.user_id);
    authFetch(`/api/train/gap-flights?${params}`, token)
      .then(d => setFlights(d.flights || []))
      .catch(() => setFlights([]))
      .finally(() => setLoading(false));
  }, [item, token]);

  if (!item) return null;
  const isGap = item.is_gap != null;

  return ReactDOM.createPortal(
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 9999, display: 'flex', alignItems: 'flex-end' }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--bg-card)', borderRadius: '20px 20px 0 0',
        width: '100%', maxWidth: 480, maxHeight: '80vh', overflowY: 'auto',
        padding: '20px 20px 48px', margin: '0 auto',
      }}>
        <div style={{ width: 36, height: 4, borderRadius: 2, background: 'var(--border)', margin: '0 auto 16px' }} />

        {isGap ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <div style={{ width: 10, height: 10, borderRadius: '50%', background: item.is_gap ? TC.amber : TC.green }} />
              <span style={{ fontSize: 12, fontWeight: 700, padding: '2px 9px', borderRadius: 6,
                background: item.is_gap ? TC.amberDim : TC.greenDim,
                color: item.is_gap ? TC.amber : TC.green }}>
                {item.is_gap ? 'Needs work' : 'On track'}
              </span>
            </div>
            <div style={{ fontSize: 19, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 6 }}>{item.label}</div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 10 }}>
              Limit: <strong style={{ color: 'var(--text-primary)' }}>{item.minimum}</strong>
              <span style={{ marginLeft: 10, color: item.is_gap ? TC.amber : TC.green }}>{item.demonstrated}</span>
            </div>
            <div style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6, background: 'var(--bg-body)', borderRadius: 10, padding: 12, marginBottom: 16 }}>{item.detail}</div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 19, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>{item.category}</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: scoreColor(item.avg_pct), fontFamily: 'var(--font-mono)', marginBottom: 10 }}>{item.avg_pct}% avg over {item.flights} flight{item.flights !== 1 ? 's' : ''}</div>
            <div style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6, background: 'var(--bg-body)', borderRadius: 10, padding: 12, marginBottom: 16 }}>{item.detail}</div>
          </>
        )}

        {/* Flights that contributed */}
        {isGap && !item.skillName ? (
          <div style={{ background: 'var(--bg-body)', borderRadius: 10, padding: 12 }}>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
              Flight frequency is based on your total approach count over the last 90 days — every claimed flight contributes. Aim for 10+ flights to maintain sharpness.
            </div>
          </div>
        ) : (
        <>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
          {isGap ? 'Flights with this skill · last 90 days' : 'All scored flights · last 90 days'}
        </div>
        {loading && <div style={{ padding: '12px 0', fontSize: 13, color: 'var(--text-muted)' }}>Loading flights...</div>}
        {!loading && flights && flights.length === 0 && (
          <div style={{ padding: '12px 0', fontSize: 13, color: 'var(--text-muted)' }}>No flights found in the last 90 days.</div>
        )}
        {!loading && flights && flights.map(f => (
          <div key={f.id} onClick={() => { onClose(); onNavigate(`/flight/${f.id}`); }}
            style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px', background: 'var(--bg-body)', borderRadius: 10, border: '1px solid var(--border)', marginBottom: 6, cursor: 'pointer' }}>
            <div style={{ width: 34, height: 34, borderRadius: 8, flexShrink: 0,
              background: f.grade === 'A' ? '#bbf7e0' : f.grade === 'B' ? '#bfdbfe' : '#fef08a',
              color: f.grade === 'A' ? '#065f46' : f.grade === 'B' ? '#1e3a8a' : '#713f12',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 800 }}>
              {f.grade || '?'}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>{f.callsign}</div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 1 }}>
                {f.arr_airport}{f.runway_id ? ` · RWY ${f.runway_id}` : ''} · {formatDate(f.flight_date)}
              </div>
              {isGap && f.skill_detail && (
                <div style={{ fontSize: 11, color: TC.amber, marginTop: 1 }}>{f.skill_detail}</div>
              )}
              {!isGap && f.deductions && f.deductions.length > 0 && (
                <div style={{ fontSize: 11, color: TC.amber, marginTop: 1 }}>{f.deductions[0]}</div>
              )}
            </div>
            <div style={{ flexShrink: 0, textAlign: 'right' }}>
              {isGap
                ? <div style={{ fontSize: 15, fontWeight: 700, fontFamily: 'var(--font-mono)', color: scoreColor(f.percentage) }}>{f.percentage}%</div>
                : <div style={{ fontSize: 15, fontWeight: 700, fontFamily: 'var(--font-mono)', color: scoreColor(f.cat_pct) }}>{f.cat_pct != null ? `${f.cat_pct}%` : '—'}</div>
              }
              <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{isGap ? 'overall' : 'cat score'}</div>
            </div>
          </div>
        ))}
        </>
        )}
      </div>
    </div>,
    document.body
  );
}

// ── Student picker ────────────────────────────────────────────────────────────
function TrainStudentPicker({ students, onClose }) {
  const { setViewingStudent } = useStudentView();
  return (
    <TOverlay onClose={onClose}>
      <div style={{ fontSize: 17, fontWeight: 700, color: TC.text, marginBottom: 4 }}>View student plan</div>
      <div style={{ fontSize: 13, color: TC.muted, marginBottom: 16 }}>Select a student to view their proficiency plan</div>
      {students.map(s => (
        <div key={s.user_id} onClick={() => { setViewingStudent(s); onClose(); }}
          style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 10, marginBottom: 8, cursor: 'pointer', background: TC.bg, border: `1px solid ${TC.border}` }}>
          <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 14, fontWeight: 700, flexShrink: 0 }}>
            {(s.display_name || s.username || '?').charAt(0).toUpperCase()}
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, color: TC.text }}>{s.display_name || s.username}</div>
            <div style={{ fontSize: 12, color: TC.dim }}>Tap to view plan</div>
          </div>
        </div>
      ))}
    </TOverlay>
  );
}

// ── Main TrainPage ────────────────────────────────────────────────────────────
function TrainPage() {
  const navigate = useNavigate();
  const { user, token, settings, refreshUser } = useAuth();
  const { viewingStudent, myStudents } = useStudentView();
  const [loading, setLoading]     = useState(true);
  const [data, setData]           = useState(null);
  const [planData, setPlanData]   = useState(null);
  const [profile, setProfile]     = useState(null);
  const [missionKey, setMissionKey] = useState(null);
  const [missions, setMissions]   = useState([]);
  const missionKeySetByUser = useRef(false);
  const [showStudentPicker, setShowStudentPicker] = useState(false);
  const [showMissionPicker, setShowMissionPicker] = useState(false);
  const [showCatalogue, setShowCatalogue] = useState(false);
  const [pilotLevel, setPilotLevel] = useState(null);
  const [detailItem, setDetailItem] = useState(null);
  const [openTile, setOpenTile]   = useState(null);
  const [acsFocused, setAcsFocused] = useState(false);
  const acsClearRef = useRef(null);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (!missionKeySetByUser.current && settings?.mission_key) setMissionKey(settings.mission_key);
  }, [settings?.mission_key]);

  useEffect(() => { if (!user) navigate('/login'); }, [user, navigate]);

  const loadData = useCallback(() => {
    if (!token) return;
    setLoading(true);
    const sp = viewingStudent ? `?as_student=${viewingStudent.user_id}` : '';
    Promise.all([
      authFetch(`/api/train/data${sp}`, token),
      authFetch(viewingStudent ? `/api/user/profile?as_student=${viewingStudent.user_id}` : '/api/user/profile', token),
      authFetch('/api/user/minimums', token),
      authFetch('/api/train/missions', token),
      authFetch(`/api/train/plan${sp}`, token),
    ]).then(([trainRaw, profData, minsData, missionsData, planRaw]) => {
      setData({ ...trainRaw, pilot_minimums: minsData?.minimums || {} });
      setPlanData({ ...planRaw, pilot_minimums: minsData?.minimums || {} });
      setProfile(profData?.profile || null);
      const loadedMissions = missionsData?.missions || [];
      setMissions(loadedMissions);
      const rawKey = missionsData?.current_key || trainRaw?.mission_key || null;
      let resolvedKey = rawKey;
      if (rawKey != null) {
        const byKey = loadedMissions.find(m => m.key === String(rawKey));
        const byId  = loadedMissions.find(m => String(m.id) === String(rawKey));
        resolvedKey = (byKey || byId)?.key || String(rawKey);
      }
      setMissionKey(resolvedKey);
    }).catch(console.error).finally(() => setLoading(false));
  }, [token, viewingStudent]);

  useEffect(loadData, [loadData]);

  useEffect(() => {
    if (!token) return;
    const url = viewingStudent
      ? `/api/pwa/pilot-level?as_student=${viewingStudent.user_id}`
      : '/api/pwa/pilot-level';
    authFetch(url, token)
      .then(d => { if (d && d.level) setPilotLevel(d); })
      .catch(() => {});
  }, [token, viewingStudent]);

  // Deep-link to ACS tab
  useEffect(() => {
    const t = searchParams.get('tab');
    if (t === 'acs') setOpenTile('acs');
    else if (t === 'practical') setOpenTile('currency');
    else if (t === 'online') setOpenTile('online');
  }, []);

  const handleMissionChange = async (key) => {
    const resolvedKey = typeof key === 'string' ? key : String(key);
    missionKeySetByUser.current = true;
    setMissionKey(resolvedKey);
    try { await authPost('/api/train/mission_key', { key: resolvedKey }, token); refreshUser(); } catch(e) {}
    setShowMissionPicker(false);
  };

  const act = async (url, body) => {
    try { await authPost(url, body, token); loadData(); } catch(e) {}
  };

  const toggleTile = (id) => {
    setOpenTile(o => o === id ? null : id);
    if (id !== 'acs') setAcsFocused(false);
  };

  const tileOpacity = (id) => openTile !== null && openTile !== id ? 0.35 : 1;

  if (loading) return <div className="loading"><div className="spinner" /><span>Loading proficiency plan...</span></div>;

  const pl = pilotLevel;
  const SKILL_API_NAMES = {
    crosswind: 'Crosswind',
    gust:      'Gust',
    ceiling:   'IFR',
    night:     'Night Flight',
    frequency: null, // no skill tag, frequency uses flight count
  };
  const gaps = buildGaps(planData).map(g => ({ ...g, skillName: SKILL_API_NAMES[g.id] }));
  const needsWork  = gaps.filter(g => g.is_gap);
  const onTrack    = gaps.filter(g => !g.is_gap);
  const weaknesses = planData?.score_weaknesses || [];
  const hasMins    = !!(planData?.pilot_minimums?.crosswind_kt || planData?.pilot_minimums?.ceiling || planData?.pilot_minimums?.gust_kt);

  const assigned    = data?.assigned_courses    || [];
  const recommended = data?.recommended_courses || [];
  const completed   = data?.completed_courses   || [];
  const dismissed   = data?.dismissed_courses   || [];
  const completedIds = new Set(completed.map(c => String(c.course_id)));
  const dismissedIds = new Set(dismissed.map(c => String(c.course_id)));
  const filteredRec  = recommended.filter(c => !completedIds.has(String(c.id)) && !dismissedIds.has(String(c.id)));
  const totalCourses = assigned.length + filteredRec.length;

  const mId = missionKey || (missions[0]?.key ?? null);
  const mission = missions.find(m => (m.key ?? m.id) === mId || String(m.id) === String(mId)) || missions[0];

  // Cert chips for mission selector
  const PILOT_CERTS  = new Set(['Private','Commercial','ATP']);
  const TYPE_RATINGS = new Set(['SEL','MEL','SES','MES']);
  const CERT_RANK    = { Private:1, Commercial:2, ATP:3 };
  const allCerts   = profile?.certificates || [];
  const allRatings = profile?.ratings || [];
  const highestCert = allCerts.filter(c => PILOT_CERTS.has(c)).sort((a,b) => (CERT_RANK[b]||0)-(CERT_RANK[a]||0))[0];
  const ratings    = allRatings.filter(r => !TYPE_RATINGS.has(r));
  const planes     = (profile?.planes_i_fly || []).slice(0,2);
  const certChips  = [...(highestCert ? [highestCert] : []), ...ratings, ...planes];

  const displayName = viewingStudent
    ? (viewingStudent.display_name || viewingStudent.username)
    : (user?.display_name || user?.username);

  const tileStyle = (id, accentColor) => ({
    background: 'var(--bg-card)', borderRadius: 16, border: '1px solid var(--border)',
    borderLeft: accentColor ? `4px solid ${accentColor}` : '1px solid var(--border)',
    overflow: 'hidden', marginBottom: 9,
    opacity: tileOpacity(id), transition: 'opacity 0.25s ease',
  });

  const tileHeadStyle = (accentColor) => ({
    padding: '12px 14px',
    display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer',
  });

  return (
    <div style={{ padding: '0 12px 12px' }}>
      <StudentBanner />

      {/* Hero */}
      <div style={{ padding: '16px 2px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
          <h2 style={{ fontSize: 21, fontWeight: 700, color: 'var(--page-bg-title)', margin: 0 }}>
            {viewingStudent ? `${displayName}'s Plan` : 'Proficiency Plan'}
          </h2>
          {myStudents.length > 0 && !viewingStudent && (
            <button onClick={() => setShowStudentPicker(true)}
              style={{ width: 24, height: 24, borderRadius: '50%', background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)', color: '#fff', border: 'none', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              👤
            </button>
          )}
        </div>
        <p style={{ fontSize: 13, color: 'var(--page-bg-sub)', margin: 0 }}>Track, improve, and stay mission-ready</p>
      </div>

      {/* Mission selector (gated by App Settings: Mission Profile Templates) */}
      {!viewingStudent && mission && (settings?.show_mission_templates !== false) && (
        <div onClick={() => setShowMissionPicker(true)} style={{
          background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border)',
          padding: '10px 13px', display: 'flex', alignItems: 'center', gap: 11,
          cursor: 'pointer', marginBottom: 9,
        }}>
          <div style={{ width: 36, height: 36, borderRadius: 9, background: TC.accentDim, border: `1px solid ${TC.accent}33`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.44 2 2 0 0 1 3.6 1.25h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.73a16 16 0 0 0 6 6l.95-.95a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.5 16"/></svg>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 3 }}>Flying as</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: certChips.length ? 4 : 0 }}>{mission.label}</div>
            {certChips.length > 0 && (
              <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                {certChips.map(c => (
                  <span key={c} style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 5, background: 'var(--bg-card)', color: 'var(--text-secondary)', border: '1px solid var(--border)' }}>{c}</span>
                ))}
              </div>
            )}
          </div>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
        </div>
      )}

      {/* ── CURRENCY TILE ── */}
      <div style={tileStyle('currency', needsWork.length > 0 ? '#ca8a04' : '#10b981')}>
        <div style={tileHeadStyle(needsWork.length > 0 ? '#fcd34d' : '#10b981')} onClick={() => toggleTile('currency')}>
          <ScoreBadge
            value={needsWork.length > 0 ? needsWork.length : '✓'}
            label={needsWork.length > 0 ? 'gaps' : 'current'}
            pct={needsWork.length > 0 ? 49 : 90}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <LabelBarred color={needsWork.length > 0 ? '#fcd34d' : '#10b981'}>Currency</LabelBarred>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
              {hasMins ? 'skill currency · 90 days' : 'Night currency + flight frequency · 90 days'}
            </div>
          </div>
          <TileChevron open={openTile === 'currency'} />
        </div>
        {openTile !== 'currency' && gaps.length > 0 && (
          <div style={{ borderTop: '1px solid var(--border)', padding: '10px 13px 12px', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {gaps.map(g => (
              <div key={g.id} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px 5px 8px', borderRadius: 20, background: 'var(--bg-body)', border: '1px solid var(--border)', fontSize: 12, fontWeight: 500, color: 'var(--text-primary)' }}>
                <div style={{ width: 7, height: 7, borderRadius: '50%', flexShrink: 0, background: g.is_gap ? '#fcd34d' : '#10b981', opacity: g.is_gap ? 1 : 0.7 }} />
                {g.label}
              </div>
            ))}
          </div>
        )}
        {openTile === 'currency' && (
          <div style={{ borderTop: '1px solid var(--border)' }}>
            {!hasMins && (
              <div style={{ padding: '10px 14px', fontSize: 12, color: 'var(--text-muted)', borderBottom: '1px solid var(--border)' }}>
                Set weather limits in <strong style={{ color: 'var(--text-primary)' }}>Profile → Personal Minimums</strong> to unlock crosswind, ceiling, and gust tracking.
              </div>
            )}
            {needsWork.length > 0 && (
              <>
                <div style={{ padding: '6px 14px 2px', fontSize: 10, fontWeight: 700, color: '#fcd34d', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Needs work</div>
                {needsWork.map(g => <GapRow key={g.id} gap={g} onTap={setDetailItem} />)}
              </>
            )}
            {onTrack.length > 0 && (
              <>
                <div style={{ padding: '6px 14px 2px', fontSize: 10, fontWeight: 700, color: '#10b981', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: needsWork.length ? 6 : 0 }}>On track</div>
                {onTrack.map(g => <GapRow key={g.id} gap={g} onTap={setDetailItem} />)}
              </>
            )}
            {needsWork.length === 0 && onTrack.length === 0 && (
              <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 14 }}>No minimums data yet</div>
            )}
          </div>
        )}
      </div>

      {/* ── SCORE WEAKNESSES TILE ── */}
      <div style={tileStyle('weaknesses', weaknesses.length > 0 ? '#ca8a04' : '#10b981')}>
          <div style={tileHeadStyle(weaknesses.length > 0 ? '#fcd34d' : '#10b981')} onClick={() => toggleTile('weaknesses')}>
            <ScoreBadge
              value={weaknesses.length > 0 ? weaknesses.length : '✓'}
              label={weaknesses.length > 0 ? 'weak' : 'solid'}
              pct={weaknesses.length > 0 ? 49 : 90}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <LabelBarred color={weaknesses.length > 0 ? '#fcd34d' : '#10b981'}>Score Weaknesses</LabelBarred>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
                {weaknesses.length > 0 ? `${weaknesses.length} categories below threshold · last ${planData?.flight_count_90d || 0} flights in 90d` : `All categories solid · last ${planData?.flight_count_90d || 0} flights in 90d`}
              </div>
            </div>
            <TileChevron open={openTile === 'weaknesses'} />
          </div>
          {openTile !== 'weaknesses' && planData?.score_categories && (
            <div style={{ borderTop: '1px solid var(--border)', padding: '10px 13px 12px', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {planData.score_categories.map(cat => {
                const weak = weaknesses.find(w => w.category === cat.category);
                const isWeak = !!weak;
                const detailItem = {
                  category: cat.category,
                  avg_pct: cat.avg_pct,
                  flights: cat.flights,
                  detail: isWeak ? weak.detail : `Averaging ${cat.avg_pct}% on ${cat.category.toLowerCase()} across ${cat.flights} scored flights. Looking solid.`,
                };
                return (
                  <div key={cat.category} onClick={() => setDetailItem(detailItem)}
                    style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px 5px 8px', borderRadius: 20, background: 'var(--bg-body)', border: '1px solid var(--border)', fontSize: 12, fontWeight: 500, color: 'var(--text-primary)', cursor: 'pointer' }}>
                    <div style={{ width: 7, height: 7, borderRadius: '50%', flexShrink: 0, background: isWeak ? '#fcd34d' : '#10b981', opacity: isWeak ? 1 : 0.7 }} />
                    {cat.category}
                    <span style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: isWeak ? '#fcd34d' : '#10b981', marginLeft: 2 }}>{cat.avg_pct}%</span>
                  </div>
                );
              })}
            </div>
          )}
          {openTile === 'weaknesses' && (
            <div style={{ borderTop: '1px solid var(--border)' }}>
              {(planData?.score_categories || []).length > 0
                ? (planData?.score_categories || []).map(cat => {
                    const weak = weaknesses.find(w => w.category === cat.category);
                    const item = {
                      category: cat.category,
                      avg_pct: cat.avg_pct,
                      flights: cat.flights,
                      detail: weak ? weak.detail : `Averaging ${cat.avg_pct}% on ${cat.category.toLowerCase()} across ${cat.flights} scored flights. Looking solid.`,
                    };
                    return <WeaknessRow key={cat.category} weakness={item} isWeak={!!weak} onTap={setDetailItem} />;
                  })
                : weaknesses.length > 0
                  ? weaknesses.map(w => <WeaknessRow key={w.category} weakness={w} isWeak={true} onTap={setDetailItem} />)
                  : <div style={{ padding: '16px 14px', fontSize: 13, color: 'var(--text-secondary)' }}>No scoring data yet.</div>
              }
            </div>
          )}
        </div>

      {/* ── ACS STANDARDS TILE ── */}
      <div style={{ ...tileStyle('acs', pl?.acs_score != null ? (pl.acs_score >= 80 ? '#10b981' : pl.acs_score >= 50 ? '#3b82f6' : '#ca8a04') : 'var(--text-muted)'), overflow: 'visible' }}>
        <div style={{ ...tileHeadStyle(pl?.acs_score != null ? (pl.acs_score >= 80 ? '#10b981' : pl.acs_score >= 50 ? 'var(--accent)' : '#fcd34d') : 'rgba(117,143,168,0.6)'), borderRadius: openTile === 'acs' ? '16px 16px 0 0' : 16 }} onClick={() => toggleTile('acs')}>
          <ScoreBadge
            value={pl?.acs_score != null ? `${Math.round(pl.acs_score)}%` : 'ACS'}
            label={pl?.acs_score != null ? 'stds' : 'stds'}
            pct={pl?.acs_score ?? null}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <LabelBarred color={pl?.acs_score != null ? (pl.acs_score >= 80 ? '#10b981' : pl.acs_score >= 50 ? 'var(--accent)' : '#fcd34d') : 'var(--text-muted)'}>ACS Standards</LabelBarred>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>Tap to review your standards</div>
          </div>
          <TileChevron open={openTile === 'acs'} />
        </div>
        {openTile !== 'acs' && pl && (
          <div style={{ borderTop: '1px solid var(--border)', padding: '11px 14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
              <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{pl.acs_task_done} / {pl.acs_task_total} tasks current</span>
              <span style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: pl.acs_score != null ? (pl.acs_score >= 80 ? '#10b981' : pl.acs_score >= 50 ? 'var(--accent)' : '#fcd34d') : 'var(--text-muted)' }}>
                {pl.acs_score != null ? `${pl.acs_score?.toFixed(0)}%` : 'No data'}
              </span>
            </div>
            <div style={{ height: 4, borderRadius: 2, background: 'var(--bg-body)', overflow: 'hidden' }}>
              <div style={{ height: '100%', borderRadius: 2, transition: 'width 0.5s',
                width: `${Math.min(100, pl.acs_score || 0)}%`,
                background: pl.acs_score >= 80 ? '#10b981' : pl.acs_score >= 50 ? 'var(--accent)' : '#fcd34d',
                opacity: 0.65,
              }} />
            </div>
            {pl.acs_task_total > 0 && (
              <div style={{ display: 'flex', gap: 6, marginTop: 9, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 9px 4px 7px', borderRadius: 20, background: 'var(--bg-body)', border: '1px solid var(--border)', fontSize: 12, fontWeight: 500, color: 'var(--text-primary)' }}>
                  <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#10b981', opacity: 0.7, flexShrink: 0 }} />
                  {pl.acs_task_done} current
                </div>
                {(pl.acs_task_total - pl.acs_task_done) > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 9px 4px 7px', borderRadius: 20, background: 'var(--bg-body)', border: '1px solid var(--border)', fontSize: 12, fontWeight: 500, color: 'var(--text-primary)' }}>
                    <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#fcd34d', flexShrink: 0 }} />
                    {pl.acs_task_total - pl.acs_task_done} due
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {openTile === 'acs' && (
          <div style={{ borderTop: '1px solid var(--border)' }}>
            <ACSSection
              missionKey={missionKey}
              missions={missions}
              onFocusChange={setAcsFocused}
              clearFocusRef={acsClearRef}
              viewingStudent={viewingStudent}
            />
          </div>
        )}
      </div>

      {/* ── ONLINE COURSES TILE ── */}
      <div style={tileStyle('online', 'var(--text-muted)')}>
        <div style={tileHeadStyle('var(--text-muted)')} onClick={() => toggleTile('online')}>
          <div style={{ width: 62, height: 62, borderRadius: 14, flexShrink: 0, background: 'rgba(117,143,168,0.08)', border: '1px solid rgba(117,143,168,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>📖</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>Online Courses</div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
              {assigned.length > 0 ? `${assigned.length} assigned · ` : ''}{filteredRec.length} recommended
            </div>
          </div>
          <TileChevron open={openTile === 'online'} />
        </div>
        {openTile === 'online' && (
          <div style={{ borderTop: '1px solid var(--border)' }}>
            {assigned.length > 0 && (
              <>
                <div style={{ padding: '6px 14px 2px', fontSize: 10, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Assigned by your school</div>
                {assigned.map((ac, i) => (
                  <div key={ac.id || ac.course_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', borderBottom: '1px solid var(--border)', cursor: 'pointer' }}>
                    <div style={{ width: 7, height: 7, borderRadius: '50%', background: ac.due_date && new Date(ac.due_date) < new Date() ? '#c0392b' : 'var(--accent)', flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ac.title}</div>
                      {ac.due_date && <div style={{ fontSize: 10, color: '#fcd34d', marginTop: 1 }}>Due {new Date(ac.due_date).toLocaleDateString('en-US',{month:'short',day:'numeric'})}</div>}
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>›</div>
                  </div>
                ))}
              </>
            )}
            {filteredRec.length > 0 && (
              <>
                <div style={{ padding: '6px 14px 2px', fontSize: 10, fontWeight: 700, color: '#fcd34d', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: assigned.length ? 6 : 0 }}>Recommended for you</div>
                {filteredRec.map(course => (
                  <div key={course.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', borderBottom: '1px solid var(--border)', cursor: 'pointer' }}>
                    <div style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--text-muted)', opacity: 0.5, flexShrink: 0 }} />
                    <div style={{ flex: 1, fontSize: 13, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{course.name}</div>
                    {course.relevance === 2 && <span style={{ fontSize: 10, fontWeight: 600, color: '#fcd34d', flexShrink: 0 }}>gap match</span>}
                    <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>›</div>
                  </div>
                ))}
              </>
            )}
            {completed.length > 0 && (
              <>
                <div style={{ padding: '6px 14px 2px', fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: 8 }}>Completed</div>
                {completed.map(c => (
                  <div key={c.course_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', borderBottom: '1px solid var(--border)', opacity: 0.7 }}>
                    <span style={{ fontSize: 14 }}>✓</span>
                    <div style={{ flex: 1, fontSize: 13, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.course_title || c.course_id}</div>
                    <button onClick={() => act('/api/train/uncomplete', { course_id: String(c.course_id) })} style={{ fontSize: 11, color: 'var(--text-muted)', background: 'none', border: '1px solid var(--border)', borderRadius: 6, padding: '2px 7px', cursor: 'pointer' }}>undo</button>
                  </div>
                ))}
              </>
            )}
            <div style={{ padding: '10px 14px' }}>
              <button onClick={() => setShowCatalogue(true)} style={{ width: '100%', padding: '10px 0', borderRadius: 9, border: '1px solid var(--border)', background: 'var(--bg-body)', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                Browse full catalogue
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Modals ── */}
      {showStudentPicker && <TrainStudentPicker students={myStudents} onClose={() => setShowStudentPicker(false)} />}

      {showMissionPicker && (
        <TOverlay onClose={() => setShowMissionPicker(false)}>
          <div style={{ fontSize: 17, fontWeight: 700, color: TC.text, marginBottom: 16 }}>How do you fly?</div>
          {missions.map(m => {
            const isActive = (m.key ?? String(m.id)) === mId;
            return (
              <div key={m.key ?? m.id} onClick={() => handleMissionChange(m.key ?? String(m.id))}
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 10, marginBottom: 8, cursor: 'pointer',
                  background: isActive ? TC.accentDim : TC.bg, border: `1px solid ${isActive ? TC.accent : TC.border}` }}>
                {m.icon && m.icon.length > 2
                  ? <span style={{ fontSize: 20, flexShrink: 0 }}>{m.icon}</span>
                  : <div style={{ width: 30, height: 30, borderRadius: '50%', background: TC.accentDim, color: TC.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 16, flexShrink: 0 }}>✈</div>
                }
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 600, color: TC.text }}>{m.label}</div>
                  <div style={{ fontSize: 12, color: TC.dim }}>{m.desc}</div>
                </div>
                {isActive && <span style={{ color: TC.accent, fontSize: 16 }}>✓</span>}
              </div>
            );
          })}
        </TOverlay>
      )}

      {detailItem && <DetailSheet item={detailItem} onClose={() => setDetailItem(null)} token={token} viewingStudent={viewingStudent} onNavigate={navigate} />}
      {showCatalogue && <CourseCatalogue onClose={() => setShowCatalogue(false)} />}
    </div>
  );
}

export { TrainPage };
export default TrainPage;
